import { setIframeSrcDoc } from 'polyfills/helpers';
import { MESSAGE_POLL_SETTINGS } from 'widget_main/app/constants';

export const appendElementToDocument = (
  element: HTMLElement,
  callback?: () => void,
) => {
  const scriptEmbedElement = document.querySelector<HTMLScriptElement>(
    '[data-kustomer-api-key]',
  );

  if (!scriptEmbedElement || !scriptEmbedElement.parentNode) return;

  scriptEmbedElement.parentNode.insertBefore(element, scriptEmbedElement);
  callback?.();
};

// eslint-disable-next-line import/prefer-default-export
export const setupIframeForApi = (
  iframeId: string,
  iframeStyles: string,
  content: string,
  iframeTitle: string,
) => {
  const iframe = document.createElement('iframe');

  iframe.setAttribute('id', iframeId);
  iframe.setAttribute('title', iframeTitle);
  iframe.setAttribute('aria-label', iframeTitle);
  iframe.setAttribute('role', 'application');
  iframe.setAttribute('tabindex', '0');

  iframe.style.cssText = iframeStyles;

  appendElementToDocument(iframe, () => {
    setIframeSrcDoc(iframe, content);
  });
};

export const getIframeForWidgetMain = (): HTMLIFrameElement | undefined => {
  const iframe = document.getElementById(
    'kustomer-ui-sdk-iframe',
  ) as HTMLIFrameElement;

  if (!iframe) return undefined;

  return iframe;
};

export const getIframeForCoreApi = () => {
  const { frameElement } = window;
  const iframe = (
    frameElement?.nodeName === 'IFRAME'
      ? frameElement
      : document.getElementById('kustomer-core-sdk-iframe')
  ) as HTMLIFrameElement | undefined;

  if (iframe?.nodeName !== 'IFRAME') return undefined;

  return iframe;
};

export const setTrackingTokenInLocalStorage = (token) => {
  try {
    localStorage.setItem('kustomer-chat-tracking-token', token);
  } catch {
    throw new Error('Setting tracking token in local storage');
  }
};

export const getTrackingTokenFromLocalStorage = () => {
  try {
    return localStorage.getItem('kustomer-chat-tracking-token');
  } catch {
    throw new Error('Getting tracking token from local storage');
  }
};

export const removeTrackingTokenFromLocalStorage = () => {
  try {
    return localStorage.removeItem('kustomer-chat-tracking-token');
  } catch {
    throw new Error('Removing tracking token from local storage');
  }
};

/// define the key for pollin ginterval local storage
const POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY =
  'kustomer-initial-chat-polling-interval-ms';

/**
 * Removes the polling interval duration (in milliseconds) from local storage.
 * This is used to clear the stored polling interval setting.
 *
 * @throws {Error} If an error occurs while removing the polling interval.
 */
export const removePollingIntervalMsFromLocalStorage = () => {
  try {
    return localStorage.removeItem(POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY);
  } catch {
    return console.warn(
      'Error removing polling interval MS from local storage',
    );
  }
};

/**
 * Retrieves the polling interval duration (in milliseconds) from local storage.
 * If the value is not found or an error occurs, it returns the default polling interval.
 *
 * @returns {number} The polling interval in milliseconds, either from local storage or the default setting.
 */
export const getPollingIntervalMsFromLocalStorage = () => {
  try {
    const pollingIntervalMsString = localStorage.getItem(
      POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY,
    );
    if (pollingIntervalMsString === null) return MESSAGE_POLL_SETTINGS.interval;
    return parseInt(pollingIntervalMsString, 10);
  } catch {
    // no matter what, return a value - in this case, default to previous behavior
    removePollingIntervalMsFromLocalStorage(); // remove, cleanup
    return MESSAGE_POLL_SETTINGS.interval;
  }
};

/**
 * Stores the polling interval duration (in milliseconds) in local storage.
 * This value determines the interval at which polling operations should occur.
 *
 * @param {string} pollingIntervalMsString - The polling interval duration in milliseconds, stored as a string.
 * @throws {Error} If an error occurs while setting the polling interval.
 */
export const setPollingIntervalMsFromLocalStorage = (
  pollingIntervalMsString: string,
) => {
  try {
    return localStorage.setItem(
      POLLING_INTERVAL_MS_LOCAL_STORAGE_KEY,
      pollingIntervalMsString,
    );
  } catch {
    return console.warn('Error setting polling interval MS in local storage');
  }
};

interface NavigatorWithStandalone extends Navigator {
  standalone: boolean;
}

/**
 * Currently only needed as a check for iOS where we are seeing sentry errors caused by embedded views
 * https://developer.mozilla.org/en-US/docs/Web/API/Navigator#navigator.standalone
 */
export const isInAppBrowser = () => {
  // this is something of a hack to get around TypeScript for a Safari/WebKit-only
  const userAgent = window.navigator.userAgent.toLowerCase();
  const ios = /iphone|ipod|ipad/.test(userAgent);
  if (!ios) return false;

  // If the device is considered "standalone", then we are in an embedded view
  return (
    'standalone' in window.navigator &&
    (window.navigator as NavigatorWithStandalone).standalone
  );
};

// In case the parent window is of a different origin
export function getSafeParentContext(windowParam: Window): Window | undefined {
  const win = windowParam ?? window;
  try {
    // Check if parent exists and has a location object accessible (won't throw cross-origin)
    if (win.parent && win.parent.location && win.parent.location.href) {
      return win.parent;
    }
  } catch (e) {
    return undefined;
  }

  // No parent or parent is the same window
  return undefined;
}
